import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { api } from "@/lib/api"
import { useSettings } from "@/hooks/useSettings"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FieldError } from "@/components/ui/field-error"
import { Button } from "@/components/ui/button"
import { ImageUpload } from "@/components/admin/image-upload"

export const Route = createFileRoute("/admin/_authed/settings")({
  component: SettingsPage,
})

type FormState = {
  site_name: string
  tagline: string
  meta_description: string
  seo_title: string
  seo_description: string
  seo_keywords: string
  contact_email: string
  contact_phone: string
  contact_address: string
  map_embed_url: string
  about_content: string
  mission_statement: string
  vision_statement: string
  hero_heading: string
  hero_subheading: string
  hero_cta_text: string
  hero_cta_link: string
  facebook: string
  twitter: string
  linkedin: string
  instagram: string
  github: string
}

const empty: FormState = {
  site_name: "",
  tagline: "",
  meta_description: "",
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  contact_email: "",
  contact_phone: "",
  contact_address: "",
  map_embed_url: "",
  about_content: "",
  mission_statement: "",
  vision_statement: "",
  hero_heading: "",
  hero_subheading: "",
  hero_cta_text: "",
  hero_cta_link: "",
  facebook: "",
  twitter: "",
  linkedin: "",
  instagram: "",
  github: "",
}

function SettingsPage() {
  const { data: settings, isLoading } = useSettings()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<FormState>(empty)
  const [logo, setLogo] = useState<File | null>(null)
  const [favicon, setFavicon] = useState<File | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!settings) return
    setForm({
      site_name: settings.site_name ?? "",
      tagline: settings.tagline ?? "",
      meta_description: settings.meta_description ?? "",
      seo_title: settings.seo_title ?? "",
      seo_description: settings.seo_description ?? "",
      seo_keywords: settings.seo_keywords ?? "",
      contact_email: settings.contact_email ?? "",
      contact_phone: settings.contact_phone ?? "",
      contact_address: settings.contact_address ?? "",
      map_embed_url: settings.map_embed_url ?? "",
      about_content: settings.about_content ?? "",
      mission_statement: settings.mission_statement ?? "",
      vision_statement: settings.vision_statement ?? "",
      hero_heading: settings.hero_heading ?? "",
      hero_subheading: settings.hero_subheading ?? "",
      hero_cta_text: settings.hero_cta_text ?? "",
      hero_cta_link: settings.hero_cta_link ?? "",
      facebook: settings.social_links?.facebook ?? "",
      twitter: settings.social_links?.twitter ?? "",
      linkedin: settings.social_links?.linkedin ?? "",
      instagram: settings.social_links?.instagram ?? "",
      github: settings.social_links?.github ?? "",
    })
  }, [settings])

  const mutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append("_method", "PUT")
      Object.entries(form).forEach(([key, value]) => fd.append(key, value))
      if (logo) fd.append("logo", logo)
      if (favicon) fd.append("favicon", favicon)
      await api.post("/admin/settings", fd, { headers: { "Content-Type": "multipart/form-data" } })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] })
      setFieldErrors({})
      toast.success("Settings saved.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  if (isLoading) {
    return <div className="h-96 animate-pulse rounded-2xl bg-secondary" />
  }

  const field = (key: keyof FormState, labelText: string, multiline = false) => (
    <div>
      <Label htmlFor={key}>{labelText}</Label>
      {multiline ? (
        <Textarea
          id={key}
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          aria-invalid={!!fieldErrors[key]}
        />
      ) : (
        <Input
          id={key}
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          aria-invalid={!!fieldErrors[key]}
        />
      )}
      <FieldError message={fieldErrors[key]} />
    </div>
  )

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl font-bold">Settings</h1>
      <p className="mt-1 text-sm text-muted-foreground">Site-wide content, SEO and contact information.</p>

      <form
        className="mt-8 space-y-10"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          mutation.mutate()
        }}
      >
        <section className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-semibold">General</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {field("site_name", "Site name")}
            {field("tagline", "Tagline")}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <ImageUpload label="Logo" currentUrl={settings?.logo_url} onChange={setLogo} />
            <ImageUpload label="Favicon" currentUrl={settings?.favicon_url} onChange={setFavicon} />
          </div>
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-semibold">Hero</h2>
          {field("hero_heading", "Hero heading")}
          {field("hero_subheading", "Hero subheading", true)}
          <div className="grid gap-4 sm:grid-cols-2">
            {field("hero_cta_text", "Hero CTA text")}
            {field("hero_cta_link", "Hero CTA link")}
          </div>
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-semibold">About</h2>
          {field("about_content", "About content (HTML)", true)}
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-semibold">Mission &amp; Vision</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {field("mission_statement", "Mission statement", true)}
            {field("vision_statement", "Vision statement", true)}
          </div>
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-semibold">SEO</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {field("seo_title", "SEO title")}
            {field("seo_keywords", "SEO keywords")}
          </div>
          {field("seo_description", "SEO description", true)}
          {field("meta_description", "Meta description", true)}
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-semibold">Contact</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {field("contact_email", "Email")}
            {field("contact_phone", "Phone")}
          </div>
          {field("contact_address", "Address")}
          <div>
            {field("map_embed_url", "Map embed URL")}
            <p className="mt-1 text-xs text-muted-foreground">
              Must be an embeddable URL (Google Maps → Share → Embed a map → copy the <code>src</code>), not a
              share link like maps.app.goo.gl — share links are blocked from loading in an iframe.
            </p>
          </div>
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-semibold">Social links</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {field("facebook", "Facebook")}
            {field("twitter", "Twitter")}
            {field("linkedin", "LinkedIn")}
            {field("instagram", "Instagram")}
            {field("github", "GitHub")}
          </div>
        </section>

        <Button type="submit" disabled={mutation.isPending} className="rounded-full bg-gradient-accent">
          {mutation.isPending ? "Saving..." : "Save settings"}
        </Button>
      </form>
    </div>
  )
}
